import {AfterContentInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Validators}                                                       from '@angular/forms';
import {
  ApDynformsConfigFieldset
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigTextbox
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-textbox';
import {ApDynformsValidator}                                          from '../../ap-dynforms/ap-dynforms-validator';
import {
  ApDynformsConfigComboBox
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-combobox';
import {ApDynformsComponent}                                          from '../../ap-dynforms/ap-dynforms.component';
import {map}                                                          from 'rxjs/operators';
import {InstrumentStore}                                              from '../../stores/docu/instrument.store';
import {
  ApDynformsConfigNumerictextbox
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {WorkTypesStore}                                               from '../../stores/base-data/worrktypes.store';
import {ListSortOrder, ObjectFactory}                                 from 'ts-tooling';
import {FormStore, IEntryComponent}                                   from '../../stores/layout/form.store';
import {LoginStore}                                                   from '../../stores/login/login.store';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {
  ApDynformsConfigCheckbox
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-checkbox';
import {ApDateService}                                                from '../../ap-core/services/ap-date-service';
import {nameOf}                                                       from '../../ap-core/utils/ap-name-of';
import {InstrumentEditFormData}                                       from './types/ap-instrument-edit.types';
import {
  ResourcesDataService
}                                                                     from '../../services/data/resources/resources-data.service';
import {MachineStore}                                                 from '../../stores/docu/machine.store';
import {
  ApTranslationService
}                                                                     from '../../ap-utils/service/ap-translation.service';
import IInstrument = Data.DocuContext.Instrument.IInstrument;
import IWorktypes = Data.BaseData.IWorktypes;

/**
 * Component for editing or creating an instrument.
 */
@Component({
  selector: 'ap-instruments-edit',
  template: `
    <ap-dynforms [fieldsets]="formConfig" [loading$]="loading$">
      <div class="ap-form-actions" dynforms.action>
        <button id="button_instruments_edit_cancel"
                type="button"
                kendoButton class="ap-form-button-left"
                (apSingleClick)="onCancelClick()">{{ 'Global__Cancel' | translate }}
        </button>
        <button id="button_instruments_edit_save"
                type="button"
                kendoButton [themeColor]="'primary'" class="ap-form-button-right"
                [disabled]="(isSaveDisabled$ | async)"
                (apSingleClick)="onSubmitClick()">{{ 'Global__Save' | translate }}
        </button>
      </div>
    </ap-dynforms>`,
  standalone: false
})
export class ApInstrumentsEditComponent implements OnInit, AfterContentInit, OnDestroy, IEntryComponent {
  /**
   * Implements IEntryComponent
   */
  public Caption$: BehaviorSubject<string> = new BehaviorSubject('');
  @ViewChild(ApDynformsComponent, {static: true}) dynForm: ApDynformsComponent;
  @Input() public data: IInstrument | undefined;
  public formConfig: ApDynformsConfigFieldset[] = [];
  public isNewMode = false;
  public loading$: Observable<boolean> = of(true);
  public isSaveDisabled$ = new BehaviorSubject<boolean>(true);

  private _formData: InstrumentEditFormData | undefined;
  private _subscriptions: Subscription[] = [];

  constructor(private formStore: FormStore,
              private loginStore: LoginStore,
              private machineStore: MachineStore,
              private workTypesStore: WorkTypesStore,
              private instrumentsStore: InstrumentStore,
              private dateService: ApDateService,
              private translationService: ApTranslationService,
              private resourcesDataService: ResourcesDataService) {
  }

  /**
   * when the component is loaded
   */
  public ngOnInit(): void {
    this.loading$ = combineLatest([
      this.machineStore.Loading$,
      this.workTypesStore.Loading$,
      this.instrumentsStore.Saving$
    ]).pipe(
      map(loadings => loadings.some(loading => loading))
    );
    this.isNewMode = !this.data;
    this._formData = {
      Name: this.data?.Name,
      DefaultWidth: this.data?.DefaultWidth,
      DefaultWorktype: this.data?.DefaultWorktype?.Id,
      DefaultHaPerHour: this.data?.DefaultHaPerHour,
      IsActive: this.isNewMode ? true : this.resourcesDataService.isResourceActive(this.data)
    };
    this.Caption$.next(this.isNewMode ? 'Global_Create_Instrument' : 'Global_Edit_Instrument');
    this.formBuilder();
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(
      this.dynForm.FormValues$.subscribe(() => {
        this.dynForm.form?.updateValueAndValidity();
        const formValues = this.dynForm.form?.getRawValue() as InstrumentEditFormData;
        const isSaveAvailable = JSON.stringify(formValues) !== JSON.stringify(this._formData)
          && !!this.dynForm.form?.valid;
        this.isSaveDisabled$.next(!isSaveAvailable);
      })
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(x => x?.unsubscribe());
  }

  /**
   * Submitting the changes.
   * Either creating a new object or updating the current.
   */
  public onSubmitClick(): void {
    const formData = this.dynForm.form.getRawValue() as InstrumentEditFormData;
    if (!formData) {
      return;
    }
    const worktype = this.workTypesStore.WorkTypes.find(x => x.Id === formData.DefaultWorktype);
    const instrument = ObjectFactory.Copy(this.data) ?? ({} as IInstrument);
    instrument.FarmId = this.loginStore.SelectedFarmId;
    instrument.Name = formData.Name;
    instrument.DefaultWidth = formData.DefaultWidth;
    instrument.DefaultWorktype = worktype;
    instrument.DefaultHaPerHour = formData.DefaultHaPerHour;
    instrument.IsDeleted = !formData.IsActive;
    instrument.DeletedAt = formData.IsActive ? undefined : this.dateService.getUtc();
    instrument.DeletedBy = formData.IsActive ? undefined : this.loginStore.User.Id;
    if (this.isNewMode) {
      this.instrumentsStore.createInstrument(instrument);
    } else {
      this.instrumentsStore.updateInstrument(instrument);
    }
    this.formStore.closeForm();
  }

  /**
   * Cancel a clicked eventHandler - closes form.
   */
  public onCancelClick(): void {
    this.formStore.closeForm();
  }

  /**
   * binding of validators
   */
  private formBuilder(): void {
    this.formConfig = [
      new ApDynformsConfigFieldset({
        key: 'instruments',
        config: [
          new ApDynformsConfigTextbox({
            key: nameOf<InstrumentEditFormData>('Name'),
            label: 'Docu_Ini__Description',
            value: this._formData.Name,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigCheckbox({
            key: nameOf<InstrumentEditFormData>('IsActive'),
            label: 'Global__Active',
            value: this._formData.IsActive,
            disabled$: this.machineStore.Machines$.pipe(
              map(machines => machines.some(x => !!x.DefaultInstrument && x.DefaultInstrument?.Id === this.data?.Id))
            )
          }),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<InstrumentEditFormData>('DefaultWidth'),
            label: 'Docu_Ini__WorkingWidth',
            value: this._formData.DefaultWidth,
            min: 0,
            max: 100,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              }),
              new ApDynformsValidator({
                validator: Validators.max(48),
                errorKey: 'Text_ValueInvalid'
              }),
              new ApDynformsValidator({
                validator: Validators.min(0),
                errorKey: 'Text_ValueInvalid'
              })
            ]
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<InstrumentEditFormData>('DefaultWorktype'),
            label: 'Docu_Ini__WorkType',
            value: this._formData.DefaultWorktype,
            valueField: nameOf<IWorktypes>('Id'),
            textField: nameOf<IWorktypes>('Description'),
            options: this.workTypesStore.WorkTypes$,
            valuePrimitive: true,
            translate: true,
            sort: 1,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<InstrumentEditFormData>('DefaultHaPerHour'),
            label: `${this.translationService.translate('FieldAreaPerformance')} [${this.translationService.translate('Unit_ha_per_h')}]`,
            value: this._formData.DefaultHaPerHour,
            min: 0,
            max: 99,
            decimals: 1
          })
        ]
      })
    ];
  }
}
